import { useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';

import type { CropPan } from './photo-crop';

/** How far one arrow key press, or one nudge button press, moves the photo. */
export const KEY_STEP = 0.03;
const KEY_STEP_BIG = 0.1;

const clampUnit = (v: number) => Math.min(1, Math.max(0, v));

interface Point {
  x: number;
  y: number;
}

export interface CropGestureOptions {
  zoom: number;
  maxZoom: number;
  /** How far the photo overhangs the box, as a fraction of the box size, sideways and up-down. */
  overhang: { x: number; y: number };
  setZoom: (zoom: number) => void;
  setPan: (update: (pan: CropPan) => CropPan) => void;
}

/** Drag, pinch and arrow-key handling for the crop box. */
export function useCropGestures({ zoom, maxZoom, overhang, setZoom, setPan }: CropGestureOptions) {
  const pointers = useRef(new Map<number, Point>());
  const pinch = useRef<{ distance: number; zoom: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  const spread = () => {
    const [a, b] = [...pointers.current.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const onPointerDown = (e: PointerEvent<HTMLElement>) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) pinch.current = { distance: spread(), zoom };
    setDragging(true);
  };

  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    const previous = pointers.current.get(e.pointerId);
    if (!previous) return;
    const current = { x: e.clientX, y: e.clientY };
    pointers.current.set(e.pointerId, current);
    if (pointers.current.size >= 2 && pinch.current) {
      if (maxZoom <= 1 || pinch.current.distance === 0) return;
      setZoom(
        Math.min(maxZoom, Math.max(1, pinch.current.zoom * (spread() / pinch.current.distance))),
      );
      return;
    }
    const box = e.currentTarget.getBoundingClientRect();
    const slackX = overhang.x * box.width;
    const slackY = overhang.y * box.height;
    setPan((pan) => ({
      x: slackX > 0 ? clampUnit(pan.x - (current.x - previous.x) / slackX) : 0.5,
      y: slackY > 0 ? clampUnit(pan.y - (current.y - previous.y) / slackY) : 0.5,
    }));
  };

  const onPointerUp = (e: PointerEvent<HTMLElement>) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) setDragging(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const step = e.shiftKey ? KEY_STEP_BIG : KEY_STEP;
    const move = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    }[e.key];
    if (!move) return;
    e.preventDefault();
    setPan((pan) => ({ x: clampUnit(pan.x + move[0]), y: clampUnit(pan.y + move[1]) }));
  };

  return {
    dragging,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onKeyDown,
    },
  };
}
